import { create } from "zustand";
import { persist } from "zustand/middleware";

import { isJwtExpired } from "../utils/jwt";

export type UserRole = "superuser" | "admin" | "employee";

export interface AuthUser {
  id: string;
  email: string;
  employee_id?: string | null;
  full_name: string;
  phone?: string;
  role: UserRole;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  mustChangePassword: boolean;
  setAuth: (payload: {
    user: AuthUser;
    access: string;
    refresh: string;
    must_change_password: boolean;
  }) => void;
  setTokens: (payload: { access: string; refresh: string }) => void;
  setUser: (user: AuthUser) => void;
  setMustChangePassword: (v: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      mustChangePassword: false,
      setAuth: ({ user, access, refresh, must_change_password }) =>
        set({ user, accessToken: access, refreshToken: refresh, mustChangePassword: must_change_password }),
      setTokens: ({ access, refresh }) => set({ accessToken: access, refreshToken: refresh }),
      setUser: (user) => set({ user }),
      setMustChangePassword: (v) => set({ mustChangePassword: v }),
      logout: () => set({ user: null, accessToken: null, refreshToken: null, mustChangePassword: false }),
    }),
    {
      name: "erpman-auth",
      // After rehydrating from localStorage, drop the persisted session
      // if both tokens are dead. Without this, ProtectedRoute sees an
      // accessToken + user and lets the user into a page whose first
      // API call immediately 401s, the response interceptor tries to
      // refresh, refresh also fails, and we forced-logout — same end
      // state, but with a flash of broken UI and a console full of
      // failed requests. Wiping up front lands them on /login cleanly.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const hasSession =
          state.accessToken || state.refreshToken || state.user;
        if (!hasSession) return;
        const accessDead = isJwtExpired(state.accessToken);
        const refreshDead = isJwtExpired(state.refreshToken);
        if (accessDead && refreshDead) {
          state.logout();
        }
      },
    }
  )
);
